import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

const TierCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  },
}));

const TierTag = styled(Button)(({ bgColor }) => ({
  backgroundColor: `${bgColor}20 !important`,
  color: `${bgColor} !important`,
  borderRadius: "20px",
  padding: "4px 16px",
  minWidth: "unset",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    boxShadow: "none",
    backgroundColor: `${bgColor}30 !important`,
  },
}));

const LoyaltyTiers = ({
  landingData,
  campaignDetails,
  formatNumberWithCommas,
  ORDER_SIZE,
  RESTAURANT_LEVEL_HEX_COLOR,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        py: 8,
        px: 2,
        background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
      }}
    >
      {/* Header Section */}
      <Box sx={{ maxWidth: "800px", mx: "auto", textAlign: "center", mb: 8 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "24px", sm: "28px", md: "34px" },
          }}
        >
          Unlock More Cashback Rewards!
        </Typography>

        <Typography
          sx={{
            color: "text.secondary",
            mb: 2,
            maxWidth: "800px",
            mx: "auto",
            fontSize: { xs: "16px", sm: "18px" },
          }}
        >
          Stay loyal and level up for higher reward points at every tier!
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {landingData?.loyaltyPlan?.plans?.map((plan, index) => (
            <TierTag key={index} bgColor={RESTAURANT_LEVEL_HEX_COLOR[plan.name]}>
              {plan.name}
            </TierTag>
          ))}
        </Box>
      </Box>

      {/* Tiers Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(auto-fit, minmax(300px, 400px))" },
          justifyContent: "center",
          gap: 3,
          maxWidth: "1350px",
          mx: "auto",
          px: { xs: 2, md: 4 },
        }}
      >
        {landingData?.loyaltyPlan?.plans?.map((plan, index) => (
          <TierCard key={index}>
            {/* Card Header */}
            <Box sx={{ p: 3, position: "relative", overflow: "hidden" }}>
              {/* Decorative Circle */}
              <Box
                sx={{
                  position: "absolute",
                  top: -32,
                  right: -32,
                  width: 128,
                  height: 128,
                  borderRadius: "50%",
                  bgcolor: `${RESTAURANT_LEVEL_HEX_COLOR[plan.name]}10`,
                  opacity: 0.5,
                }}
              />

              <Box sx={{ position: "relative" }}>
                <TierTag bgColor={RESTAURANT_LEVEL_HEX_COLOR[plan.name]} sx={{ mb: 2 }}>
                  {plan.name}
                </TierTag>

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Total Meal Spend
                </Typography>

                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  {landingData?.loyaltyPlan?.plans?.length === index + 1 ? (
                    <>Above ${formatNumberWithCommas(plan.eligibleSaleAmount)}</>
                  ) : (
                    <>
                      ${formatNumberWithCommas(plan.eligibleSaleAmount)} - $
                      {formatNumberWithCommas(
                        landingData?.loyaltyPlan?.plans?.[index + 1]?.eligibleSaleAmount
                      )}
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Rewards List */}
            {plan.details.map((detail, detailIndex) => (
              <Box
                key={detailIndex}
                sx={{
                  p: 3,
                  bgcolor: detailIndex % 2 === 0 ? "grey.50" : "white",
                  borderTop: "1px solid",
                  borderColor: "grey.100",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {plan.details.length > 1 ? detail.name : ORDER_SIZE[detail.name]}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "success.main",
                      }}
                    >
                      {detail.cashbackMultiple_100}
                    </Typography>
                    <Typography color="text.secondary">X</Typography>

                    {campaignDetails?.campaign_campaign_details[0]?.rewardType === "MULTIPLIER" && (
                      <>
                        <Typography color="text.secondary">+</Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "success.main",
                          }}
                        >
                          {campaignDetails.campaign_campaign_details[0].multiplierCashback}
                        </Typography>
                        <Typography color="text.secondary">X</Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {detail.minimumSale !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    {detail.maximumSale ? (
                      <>
                        ${formatNumberWithCommas(detail.minimumSale)} - $
                        {formatNumberWithCommas(detail.maximumSale)}
                      </>
                    ) : (
                      <>≥ ${formatNumberWithCommas(detail.minimumSale)}</>
                    )}
                  </Typography>
                )}
              </Box>
            ))}
          </TierCard>
        ))}
      </Box>
    </Box>
  );
};

LoyaltyTiers.propTypes = {
  landingData: PropTypes.shape({
    loyaltyPlan: PropTypes.shape({
      plans: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          eligibleSaleAmount: PropTypes.number.isRequired,
          details: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string.isRequired,
              minimumSale: PropTypes.number,
              maximumSale: PropTypes.number,
              cashbackMultiple_100: PropTypes.number.isRequired,
            })
          ).isRequired,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  campaignDetails: PropTypes.shape({
    campaign_campaign_details: PropTypes.arrayOf(
      PropTypes.shape({
        rewardType: PropTypes.string,
        multiplierCashback: PropTypes.number,
      })
    ),
  }),
  formatNumberWithCommas: PropTypes.func.isRequired,
  ORDER_SIZE: PropTypes.object.isRequired,
  RESTAURANT_LEVEL_HEX_COLOR: PropTypes.object.isRequired,
};

export default LoyaltyTiers;
